import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError, formatDate, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Plus, Paperclip, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export function SupportList() {
  const [tickets, setTickets] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/tickets");
      setTickets(data);
    } catch (err) {
      console.error("Failed to load tickets:", err);
    }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="section-label mb-3">— SUPPORT CENTER</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="support-headline">My Tickets</h1>
            <p className="font-plex text-[#9CA3AF] mt-3 max-w-2xl">Direct line to Dry Ice Bulls. Only you and our admins see these conversations.</p>
          </div>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-ticket-btn">
            <Plus size={16} weight="bold" /> New Ticket
          </button>
        </div>

        {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={refresh} />}

        {loading ? (
          <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>
        ) : tickets.length === 0 ? (
          <div className="surface p-12 text-center" data-testid="empty-tickets">
            <div className="font-chivo font-bold text-xl text-white mb-2">No tickets yet</div>
            <div className="font-plex text-sm text-[#9CA3AF] mb-6">Submit a question, request equipment support, or ask about anything.</div>
            <button onClick={() => setShowNew(true)} className="btn-primary"><Plus size={16} weight="bold" /> Open A Ticket</button>
          </div>
        ) : (
          <div className="surface divide-y divide-[#262626]" data-testid="tickets-list">
            {tickets.map((t) => (
              <Link to={`/support/${t.id}`} key={t.id} className="block p-5 hover:bg-[#121212] transition-colors" data-testid={`ticket-${t.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`tag ${t.status === "open" ? "tag-active" : ""}`}>{t.status}</span>
                      <span className="tag">{t.priority}</span>
                      <span className="font-mono text-xs text-[#52525B]">{t.category}</span>
                    </div>
                    <div className="font-chivo font-bold text-white truncate">{t.subject}</div>
                    {t.user_name && (
                      <div className="font-mono text-xs text-[#52525B] mt-1">From: {t.user_name} · {t.user_email}</div>
                    )}
                  </div>
                  <div className="font-mono text-xs text-[#52525B] shrink-0">{formatDate(t.updated_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ subject: "", category: "Equipment Support", priority: "normal", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/tickets", form);
      toast.success("Ticket created");
      onCreated();
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="new-ticket-modal">
      <div className="w-full max-w-xl surface p-8" onClick={(e) => e.stopPropagation()}>
        <div className="section-label mb-2">— NEW TICKET</div>
        <h2 className="font-chivo font-black text-2xl text-white uppercase mb-6">Submit a Question</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Subject</label>
            <input value={form.subject} onChange={onChange("subject")} required className="input-rugged" data-testid="ticket-subject-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Category</label>
              <select value={form.category} onChange={onChange("category")} className="input-rugged" data-testid="ticket-category-select">
                <option>Equipment Support</option>
                <option>Business / Application</option>
                <option>Account / Billing</option>
                <option>Inventory Inquiry</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Priority</label>
              <select value={form.priority} onChange={onChange("priority")} className="input-rugged" data-testid="ticket-priority-select">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Message</label>
            <textarea value={form.message} onChange={onChange("message")} required rows={6} className="input-rugged resize-none" data-testid="ticket-message-input" />
          </div>
          {error && <div className="text-sm font-mono text-[#EF4444]">{error}</div>}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-ticket-btn">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="submit-ticket-btn">
              {loading ? "Sending..." : "Submit Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function SupportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setData(data);
    } catch (err) {
      console.error("Failed to load ticket:", err);
      navigate("/support");
    }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, [id]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${id}/messages`, { message: reply });
      setReply("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/tickets/${id}`, { status });
      toast.success("Updated");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Layout><div className="max-w-4xl mx-auto p-10 font-mono text-xs text-[#52525B]">[LOADING]</div></Layout>;
  if (!data) return null;

  const { ticket, messages } = data;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
        <Link to="/support" className="font-mono text-xs text-[#00D4FF] mb-4 inline-block hover:underline" data-testid="back-to-tickets">← BACK TO TICKETS</Link>
        <div className="surface p-6 mb-6" data-testid="ticket-detail-header">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`tag ${ticket.status === "open" ? "tag-active" : ""}`}>{ticket.status}</span>
                <span className="tag">{ticket.priority}</span>
                <span className="font-mono text-xs text-[#52525B]">{ticket.category}</span>
              </div>
              <h1 className="font-chivo font-black text-2xl lg:text-3xl text-white uppercase">{ticket.subject}</h1>
              <div className="font-mono text-xs text-[#52525B] mt-2">
                Opened by {ticket.user_name || ticket.user_email} · {formatDate(ticket.created_at)}
              </div>
            </div>
            {isAdmin && (
              <div className="flex flex-col gap-2">
                <select value={ticket.status} onChange={(e) => setStatus(e.target.value)} className="input-rugged text-xs py-2" data-testid="admin-status-select">
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 mb-6" data-testid="messages-list">
          {messages.map((m) => {
            const isAdminMsg = m.author_role === "admin";
            return (
              <div key={m.id} className={`surface p-5 ${isAdminMsg ? "border-l-2 border-l-[#00D4FF]" : "border-l-2 border-l-[#FF5722]"}`} data-testid={`message-${m.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`font-chivo font-bold ${isAdminMsg ? "text-[#00D4FF]" : "text-white"}`}>
                      {isAdminMsg ? "Dry Ice Bulls" : m.author_name}
                    </span>
                    <span className="tag">{isAdminMsg ? "ADMIN" : "MEMBER"}</span>
                  </div>
                  <div className="font-mono text-xs text-[#52525B]">{formatDate(m.created_at)}</div>
                </div>
                <div className="font-plex text-sm text-[#9CA3AF] whitespace-pre-wrap leading-relaxed">{m.message}</div>
                {m.attachments && m.attachments.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {m.attachments.map((a) => (
                      <a key={a.id} href={`${BACKEND_URL}/api/files/${a.id}/download`} target="_blank" rel="noreferrer" className="tag tag-active">
                        <Paperclip size={10} weight="bold" /> {a.filename}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <form onSubmit={sendReply} className="surface p-5" data-testid="reply-form">
          <div className="section-label mb-3">REPLY</div>
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={5} placeholder="Type your message..." className="input-rugged resize-none mb-3" data-testid="reply-textarea" />
          <div className="flex justify-end">
            <button type="submit" disabled={sending || !reply.trim()} className="btn-primary" data-testid="send-reply-btn">
              <PaperPlaneTilt size={14} weight="bold" /> {sending ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
